import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, FormControl, InputLabel, Select, MenuItem, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert, Stack, Paper, Link, Divider, Tooltip,
} from '@mui/material';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import { api, apiRequest } from '../../utils/api';

const TEAL = '#2D6E6A';
const TEAL_DARK = '#1F4E4A';

// ── Types ────────────────────────────────────────────────────────────────────
interface ReportRow {
  report_id: string;
  content_type: string;
  content_id: string;
  reason_code: string;
  reason_detail: string | null;
  status: string;
  priority: string;
  created_at: string;
  acknowledged_at: string | null;
  preservation_hold: boolean;
  ncmec_report_id: string | null;
  ncmec_reported_at: string | null;
}

interface ReportAction {
  action_id: string;
  action: string;
  external_ref: string | null;
  note: string | null;
  created_at: string;
  admin_name: string | null;
}

interface ReportDetail extends ReportRow {
  reporter_name?: string | null;
  reporter_email?: string | null;
  reviewed_by_name?: string | null;
  acknowledged_by_name?: string | null;
}

// ── Constants ──────────────────────────────────────────────────────────────
const REASON_LABELS: Record<string, string> = {
  csam: 'Child sexual abuse (CSAM)', illegal: 'Illegal content', harassment: 'Harassment',
  hate: 'Hate speech', violence: 'Violence', sexual: 'Sexual content', spam: 'Spam', other: 'Other',
};

const ACTION_LABELS: Record<string, string> = {
  acknowledged: 'Acknowledged', content_contained: 'Content contained', content_released: 'Content released',
  account_suspended: 'Account suspended', preserved: 'Preserved (hold)', reported_to_ncmec: 'Reported to NCMEC',
  note: 'Note added', status_change: 'Status changed', reopened: 'Reopened',
};

const STATUS_OPTIONS = ['open', 'reviewing', 'actioned', 'dismissed'];
const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : '\u2014');

// ── Component ────────────────────────────────────────────────────────────────
const TrustSafetyTab: React.FC = () => {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reasonFilter, setReasonFilter] = useState('');

  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [actions, setActions] = useState<ReportAction[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ncmecRef, setNcmecRef] = useState('');
  const [note, setNote] = useState('');
  const [editStatus, setEditStatus] = useState('');

  // The escalation queue is high-priority reports (csam/illegal land here first).
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/content-reports?priority=high');
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load reports');
      const rows: ReportRow[] = data.reports || [];
      setReports(reasonFilter ? rows.filter((r) => r.reason_code === reasonFilter) : rows);
    } catch (e: any) {
      setError(e.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [reasonFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (reportId: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/content-reports/${reportId}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load report');
      setDetail(data.report);
      setActions(data.actions || []);
      setNcmecRef(data.report.ncmec_report_id || '');
      setNote('');
      setEditStatus(data.report.status);
    } catch (e: any) {
      setError(e.message || 'Failed to load report');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { if (!busy) { setDetail(null); setActions([]); } };

  // Single action dispatcher — every button posts to the deployed PATCH route.
  const doAction = async (payload: Record<string, any>) => {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiRequest(`/admin/content-reports/${detail.report_id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Action failed');
      await openDetail(detail.report_id); // refresh detail + timeline
      await load();                        // refresh queue
    } catch (e: any) {
      setError(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const unackedCount = reports.filter((r) => !r.acknowledged_at).length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <GppMaybeIcon sx={{ color: TEAL }} />
        <Typography variant="h6" fontWeight={600}>Trust &amp; Safety</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        High-priority escalation queue. Child-safety (CSAM) and illegal-content reports are
        preserved and contained automatically on receipt. Follow the runbook on each report.
        The reported material is never displayed here — it is referenced by ID only.
      </Typography>

      {unackedCount > 0 && (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          {unackedCount} high-priority report{unackedCount > 1 ? 's' : ''} awaiting acknowledgment.
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Reason</InputLabel>
          <Select label="Reason" value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}>
            <MenuItem value="">All high-priority</MenuItem>
            <MenuItem value="csam">CSAM</MenuItem>
            <MenuItem value="illegal">Illegal content</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={load} sx={{ color: TEAL, borderColor: TEAL }}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : reports.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No high-priority reports in the queue.
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reason</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ack</TableCell>
                <TableCell>Hold</TableCell>
                <TableCell>NCMEC</TableCell>
                <TableCell>Reported</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => {
                const unacked = !r.acknowledged_at;
                return (
                  <TableRow key={r.report_id} sx={unacked ? { bgcolor: 'rgba(211,47,47,0.06)' } : undefined}>
                    <TableCell>
                      <Chip size="small" color="error"
                        variant={r.reason_code === 'csam' ? 'filled' : 'outlined'}
                        label={REASON_LABELS[r.reason_code] || r.reason_code} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.content_type}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.content_id.slice(0, 8)}…</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={r.status} /></TableCell>
                    <TableCell>{r.acknowledged_at
                      ? <Chip size="small" color="success" label="Yes" />
                      : <Chip size="small" color="warning" label="No" />}</TableCell>
                    <TableCell>{r.preservation_hold
                      ? <Tooltip title="Evidence preserved; account cannot be purged"><LockIcon fontSize="small" sx={{ color: TEAL }} /></Tooltip>
                      : '\u2014'}</TableCell>
                    <TableCell>{r.ncmec_report_id
                      ? <Chip size="small" color="success" label="Filed" />
                      : '\u2014'}</TableCell>
                    <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={() => openDetail(r.report_id)}
                        sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── Per-report runbook drawer ─────────────────────────────────────── */}
      <Dialog open={!!detail} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GppMaybeIcon sx={{ color: TEAL }} /> Escalation runbook
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Alert severity="warning" icon={<LockIcon />}>
                Do not download, copy, save, or forward the reported material. Work from the
                content reference only. Report it to NCMEC and let them retrieve it.
              </Alert>

              {/* Report facts — pointer only, no media */}
              <Box>
                <Typography variant="caption" color="text.secondary">Report</Typography>
                <Typography variant="body2">
                  {REASON_LABELS[detail.reason_code] || detail.reason_code} &middot; {detail.content_type} &middot; {detail.content_id}
                </Typography>
                {detail.reason_detail && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{detail.reason_detail}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Filed {fmt(detail.created_at)}
                  {detail.acknowledged_at ? ` \u00b7 acknowledged ${fmt(detail.acknowledged_at)}${detail.acknowledged_by_name ? ` by ${detail.acknowledged_by_name}` : ''}` : ' \u00b7 not yet acknowledged'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Reporter: {detail.reporter_name || detail.reporter_email || 'Anonymous'}
                </Typography>
              </Box>

              <Divider />

              {/* Step 1 — acknowledge */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>1. Acknowledge</Typography>
                <Typography variant="caption" color="text.secondary">Confirm a human has picked this up. Logged to the chain of custody.</Typography>
                <Box sx={{ mt: 1 }}>
                  <Button size="small" variant="contained" disabled={busy || !!detail.acknowledged_at}
                    onClick={() => doAction({ action: 'acknowledged' })}
                    sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
                    {detail.acknowledged_at ? 'Acknowledged' : 'Acknowledge'}
                  </Button>
                </Box>
              </Box>

              {/* Step 2 — containment */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>2. Contain content</Typography>
                <Typography variant="caption" color="text.secondary">
                  {detail.content_type === 'photo'
                    ? 'Hide the photo from all feeds (auto-applied on a high-priority report; re-apply or release here).'
                    : 'Containment flag applies to photos. For a profile, suspend the account via the Users tab.'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="contained" disabled={busy || detail.content_type !== 'photo'}
                    onClick={() => doAction({ action: 'content_contained' })}
                    sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
                    Contain
                  </Button>
                  <Button size="small" variant="outlined" disabled={busy || detail.content_type !== 'photo'}
                    onClick={() => doAction({ action: 'content_released' })}
                    sx={{ color: TEAL, borderColor: TEAL }}>
                    Release
                  </Button>
                </Stack>
              </Box>

              {/* Step 3 — NCMEC */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>3. Report to NCMEC</Typography>
                <Stack spacing={0.5} sx={{ mb: 1 }}>
                  <Link href="https://report.cybertip.org" target="_blank" rel="noopener" sx={{ color: TEAL, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    File a CyberTipline report <OpenInNewIcon fontSize="inherit" />
                  </Link>
                  <Link href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener" sx={{ color: TEAL, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    NCMEC CyberTipline info <OpenInNewIcon fontSize="inherit" />
                  </Link>
                  <Typography variant="caption" color="text.secondary">24/7 hotline: 1-800-THE-LOST (1-800-843-5678)</Typography>
                </Stack>
                {detail.ncmec_report_id ? (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    Filed {fmt(detail.ncmec_reported_at)} &middot; report # {detail.ncmec_report_id}
                  </Alert>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" label="NCMEC report number" value={ncmecRef}
                      onChange={(e) => setNcmecRef(e.target.value)} sx={{ flex: 1 }} />
                    <Button size="small" variant="contained" disabled={busy || !ncmecRef.trim()}
                      onClick={() => doAction({ action: 'reported_to_ncmec', external_ref: ncmecRef.trim() })}
                      sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
                      Record
                    </Button>
                  </Stack>
                )}
              </Box>

              {/* Step 4 — documentation: note + status */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>4. Document &amp; resolve</Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <TextField size="small" label="Add a note (logged)" multiline minRows={2} value={note}
                    onChange={(e) => setNote(e.target.value)} />
                  <Button size="small" variant="outlined" disabled={busy || !note.trim()}
                    onClick={() => doAction({ action: 'note', note: note.trim() })}
                    sx={{ color: TEAL, borderColor: TEAL, alignSelf: 'flex-start' }}>
                    Add note
                  </Button>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Status</InputLabel>
                      <Select label="Status" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                        {STATUS_OPTIONS.map((sx) => <MenuItem key={sx} value={sx}>{cap(sx)}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <Button size="small" variant="contained" disabled={busy || editStatus === detail.status}
                      onClick={() => doAction({ status: editStatus })}
                      sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
                      Set status
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              {/* Chain-of-custody timeline */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Chain of custody</Typography>
                {actions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No actions logged yet.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {actions.map((a) => (
                      <Box key={a.action_id} sx={{ borderLeft: `3px solid ${TEAL}`, pl: 1.5, py: 0.25 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {ACTION_LABELS[a.action] || a.action}
                          {a.external_ref ? ` \u00b7 ref ${a.external_ref}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmt(a.created_at)}{a.admin_name ? ` \u00b7 ${a.admin_name}` : ' \u00b7 system'}
                        </Typography>
                        {a.note && <Typography variant="body2" sx={{ mt: 0.25 }}>{a.note}</Typography>}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail} disabled={busy}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrustSafetyTab;
